import { put, select, takeLatest, takeEvery, all, cancel } from 'redux-saga/effects';
import { notify } from '@mrlannigan/reapop';
import { sagaError } from 'helpers/handleGenericSagaError';
import apiAuthCall from 'helpers/apiAuthCall';

import { getCurrentUserIsOkta } from 'services/users/selectors';
import { setMetaChannelAvailability, requestUpsellSuccess } from 'services/entities/duck';

import { TARGETING_TACTICS_VIEW, CREATIVES_REPORT_VIEW } from 'constants/capabilities';

import { get } from 'lodash';

import {
    actions,
    fetchChannelSuccess,
    fetchChannelCreativesSuccess,
    fetchChannelDynamicCreativesSuccess,
    fetchChannelCalloutsSuccess,
    fetchChannelTargetingTacticsSuccess,
} from './duck';

import {
    channelDataEndpoint,
    channelCalloutsEndpoint,
    channelCreativesEndpoint,
    channelDynamicCreativesEndpoint,
    channelTargetingTacticsEndpoint,
    requestChannelUpsellEndpoint,
} from './selectors';

export function* fetchChannel(api, action) {
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(channelDataEndpoint, action.payload)
            }
        );

        yield put(fetchChannelSuccess(result.data));
        yield put(setMetaChannelAvailability({ [action.payload.channel]: result.data.params.hasData }));
    } catch (err) {
        yield put(notify({
            id: action.payload.transactionId,
            ...sagaError(err)
        }));
    }
}

export function* fetchChannelTargetingTactics(api, action) {
    const state = action.getState();
    // Race Condition: Current User may not have returned, so if there is no scope
    // to check against, make the API call as a fallback.
    const scope = get(state, 'users.currentUser.scope', null);
    const showTargetingTactics = get(state, 'entities.meta.meta.showTargetingTactics');
    if ((!scope || scope.includes(TARGETING_TACTICS_VIEW)) && showTargetingTactics) {
        try {
            const result = yield apiAuthCall(
                state,
                [api, api.request],
                {
                    method: 'get',
                    url: yield select(channelTargetingTacticsEndpoint, action.payload)
                }
            );

            yield put(fetchChannelTargetingTacticsSuccess(result.data));
        } catch (err) {
            yield put(notify({
                id: action.payload.transactionId,
                ...sagaError(err)
            }));
        }
    } else {
        yield cancel();
    }
}

export function* fetchChannelCreatives(api, action) {
    const state = action.getState();

    // Race Condition: Current User may not have returned, so if there is no scope
    // to check against, make the API call as a fallback.
    const scope = get(state, 'users.currentUser.scope', null);
    if (!scope || scope.includes(CREATIVES_REPORT_VIEW)) {
        try {
            const result = yield apiAuthCall(
                state,
                [api, api.request],
                {
                    method: 'get',
                    url: yield select(channelCreativesEndpoint, action.payload)
                }
            );

            yield put(fetchChannelCreativesSuccess(result.data));
        } catch (err) {
            yield put(notify({
                id: action.payload.transactionId,
                ...sagaError(err)
            }));
        }
    } else {
        yield cancel();
    }
}

export function* fetchChannelDynamicCreatives(api, action) {
    const state = action.getState();

    // Race Condition: Current User may not have returned, so if there is no scope
    // to check against, make the API call as a fallback.
    const scope = get(state, 'users.currentUser.scope', null);
    if (!scope || scope.includes(CREATIVES_REPORT_VIEW)) {
        try {
            const result = yield apiAuthCall(
                state,
                [api, api.request],
                {
                    method: 'get',
                    url: yield select(channelDynamicCreativesEndpoint, action.payload)
                }
            );

            yield put(fetchChannelDynamicCreativesSuccess(result.data));
        } catch (err) {
            yield put(notify({
                id: action.payload.transactionId,
                ...sagaError(err)
            }));
        }
    } else {
        yield cancel();
    }
}

export function* fetchChannelCallouts(api, action) {
    try {
        const result = yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'get',
                url: yield select(channelCalloutsEndpoint, action.payload)
            }
        );

        yield put(fetchChannelCalloutsSuccess(result.data));
    } catch (err) {
        yield put(notify({
            id: action.payload.transactionId,
            ...sagaError(err)
        }));
    }
}

export function* requestChannelUpsell(api, action) {
    const isOktaUser = yield select(getCurrentUserIsOkta);
    if (isOktaUser) {
        yield put(notify({
            id: action.payload.transactionId,
            status: 500,
            message: 'Goodway Group employees cannot submit channel uptake requests.'
        }));
        return null;
    }
    try {
        yield apiAuthCall(
            action.getState(),
            [api, api.request],
            {
                method: 'post',
                url: yield select(requestChannelUpsellEndpoint, action.payload)
            }
        );

        yield put(requestUpsellSuccess(action.payload));
        yield put(notify({
            id: action.payload.transactionId,
            status: 200,
            message: `Your ${action.payload.channel.toUpperCase()} channel has been requested. Goodway Group will be in contact with you soon.`
        }));
    } catch (err) {
        yield put(notify({
            id: action.payload.transactionId,
            ...sagaError(err)
        }));
    }
    return null;
}

export default function* channelSagas({ api }) {
    yield all([
        takeLatest(actions.FETCH_DATA, fetchChannel, api),
        takeLatest(actions.FETCH_CREATIVES, fetchChannelCreatives, api),
        takeLatest(actions.FETCH_DYNAMIC_CREATIVES, fetchChannelDynamicCreatives, api),
        takeLatest(actions.FETCH_TARGETING_TACTICS, fetchChannelTargetingTactics, api),
        takeEvery(actions.FETCH_CALLOUTS, fetchChannelCallouts, api),
        takeLatest(actions.REQUEST_CHANNEL_UPSELL, requestChannelUpsell, api),
    ]);
}



// WEBPACK FOOTER //
// ./src/services/channel/saga.js